import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/authContext';
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Stack,
  Skeleton,
  Button,
  Modal,
  IconButton,
  CircularProgress,
} from '@mui/material';
import SnackbarComponent from '../../utils/SnackbarComponent';
import { ExpandMore, ExpandLess, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getUserBadges } from '../../services/apiservice';
import { Helmet } from 'react-helmet-async';
import { deleteAssignment, approveBadge } from '../../services/apiservice';
import { useApiData } from '../../hooks/useApi';

const ApproveBadges = () => {
  const [badges, setBadges] = useState([]);
  const { user } = useAuth();
  const userId = user.UserID;
  const { data: fetchedBadges, loading } = useApiData(() => getUserBadges(userId).then((res) => res.data), [userId]);
  const [hoveredBadgeId, setHoveredBadgeId] = useState(null);
  const [visibleCards, setVisibleCards] = useState(3);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [loadingApprove, setLoadingApprove] = useState({});
  const [loadingReject, setLoadingReject] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (fetchedBadges) {
      setBadges(fetchedBadges);
    }
  }, [fetchedBadges]);

  const handleApproveBadge = async (badgeId) => {
    setLoadingApprove((prev) => ({ ...prev, [badgeId]: true }));
    try {
      const response = await approveBadge(badgeId);
      if (response.success) {
        setBadges((prevBadges) =>
          prevBadges.map((badge) =>
            badge.AssignmentID === badgeId ? { ...badge, AssignmentStatus: 'Active' } : badge
          )
        );
        setSnackbarMessage('Credential approved successfully!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage('Failed to approve credential');
        setSnackbarSeverity('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingApprove((prev) => ({ ...prev, [badgeId]: false }));
      setSnackbarOpen(true);
    }
  };

  const handleRejectBadge = async (badgeId) => {
    setLoadingReject((prev) => ({ ...prev, [badgeId]: true }));
    try {
      const response = await deleteAssignment(badgeId);
      if (response.success) {
        setBadges((prevBadges) => prevBadges.filter((badge) => badge.AssignmentID !== badgeId));
        setSnackbarMessage('Credential rejected successfully!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage('Failed to reject credential.');
        setSnackbarSeverity('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingReject((prev) => ({ ...prev, [badgeId]: false }));
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const renderSkeleton = () => (
    <Grid item xs={12} sm={6} md={4}>
      <Card sx={{ width: '100%', borderRadius: 4, boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <Skeleton
          variant="circular"
          width={150}
          height={150}
          sx={{ margin: '0 auto', mt: 2, borderRadius: '50%' }}
        />
        <CardContent>
          <Skeleton variant="text" width="60%" sx={{ height: '40px' }} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="80%" />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginTop: 2 }}>
            <Stack direction="row" spacing={1}>
              <Skeleton variant="rectangular" width={60} height={30} />
              <Skeleton variant="rectangular" width={60} height={30} />
            </Stack>
            <Skeleton variant="rectangular" width={60} height={25} />
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );

  const handleShowMore = () => {
    setVisibleCards((prev) => Math.min(prev + 3, badges.length));
  };

  const handleShowLess = () => {
    setVisibleCards(3);
  };

  const notApprovedBadges = badges.filter((badge) => badge.AssignmentStatus === 'Assigned');
  const showMoreAvailable = notApprovedBadges.length > 3;

  return (
    <Box sx={{ padding: 4 }}>

      <Helmet>
        <title>Approve Badges</title>
      </Helmet>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <Typography
        variant="h4"
        component="h2"
        sx={{
          textAlign: 'center',
          fontWeight: 700,
          fontSize: '2.5rem',
          color: '#454545',
          marginBottom: 4,
          position: 'relative',
          fontFamily: 'Roboto, sans-serif',
        }}
      >
        Approve Credentials
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: '-10px',
            width: '36%',
            height: '3px',
            backgroundColor: '#42A00E',
            transform: 'translateX(-50%)',
          }}
        />
      </Typography>

      {loading ? (
        <Grid container spacing={2}>
          {Array.from(new Array(6)).map((_, index) => (
            <React.Fragment key={index}>{renderSkeleton()}</React.Fragment>
          ))}
        </Grid>
      ) : (
        <>
          {notApprovedBadges.length === 0 ? (
            <Typography variant="h6" sx={{ textAlign: 'center', marginTop: 4, color: 'red' }}>
              No credentials to approve.
            </Typography>
          ) : (
            <Grid container spacing={4}>
              {notApprovedBadges.slice(0, visibleCards).map((badge) => {
                const isHovered = hoveredBadgeId === badge.AssignmentID;

                return (
                  <Grid item xs={12} sm={6} md={4} key={badge.AssignmentID}>
                    <Card
                      onMouseEnter={() => setHoveredBadgeId(badge.AssignmentID)}
                      onMouseLeave={() => setHoveredBadgeId(null)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 4,
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        background: 'linear-gradient(145deg, #f5f7fa, #c3cfe2)',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.2)',
                        },
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={badge.CredentialImageURL}
                        alt={badge.CredentialName}
                        sx={{
                          height: 160,
                          objectFit: 'contain',
                          padding: 2,
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography
                          gutterBottom
                          variant="h6"
                          component="div"
                          sx={{
                            maxHeight: '1.5em',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {badge.CredentialName}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxHeight: 'none',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: isHovered ? 'none' : 2,
                            WebkitBoxOrient: 'vertical',
                            flexGrow: 1,
                          }}
                        >
                          {badge.CredentialDescription}
                        </Typography>

                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginTop: 2 }}>
                          <Stack direction="row" spacing={1}>
                            <Button variant="contained" onClick={() => handleApproveBadge(badge.AssignmentID)} disabled={loadingApprove[badge.AssignmentID]}>
                              {loadingApprove[badge.AssignmentID] ? <CircularProgress size={24} color="inherit" /> : 'Approve'}
                            </Button>
                            <Button variant="outlined" color="secondary" onClick={() => handleRejectBadge(badge.AssignmentID)} disabled={loadingReject[badge.AssignmentID]}>
                              {loadingReject[badge.AssignmentID] ? <CircularProgress size={24} color="inherit" /> : 'Reject'}
                            </Button>
                          </Stack>
                          <Chip
                            label={badge.AssignmentStatus}
                            color="warning"
                            sx={{
                              color: 'white',
                              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
                              borderRadius: 1,
                            }}
                          />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {showMoreAvailable && (
            <Box sx={{ textAlign: 'center', marginTop: 4 }}>
              {visibleCards < notApprovedBadges.length ? (
                <Button variant="contained" onClick={handleShowMore}>
                  Show More <ExpandMore />
                </Button>
              ) : (
                <Button variant="contained" onClick={handleShowLess}>
                  Show Less <ExpandLess />
                </Button>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ApproveBadges